import { DEFAULT_USER_DATA } from "@/constants/defaults"
import { TLoginUserData } from "@/schemas/user.schema"
import { create, createStore, useStore } from "zustand"
import { setCookie } from "cookies-next/client"
import { persist } from "zustand/middleware"
import { createContext, useContext, useRef } from "react"
import { appStore$ } from "@/stores/app.v2.store"

interface TAppStore {
  isSidebarMenuExpanded: boolean
  userData: TLoginUserData & { xAuth: string }
  _old_webphone: boolean
  webphoneSelectedContactID?: string
  webphoneExpanded: boolean
  webphonePoppedOut: boolean
  webphonePopup: {
    height: number
    width: number
  }
}

interface TAppStoreWithFunctions extends TAppStore {
  changeSidebarMenuState: (setExpanded: boolean) => void
  setUserData: (userData: TLoginUserData & { xAuth: string }) => void
  setWebphoneExpanded: (expanded: boolean) => void
  setWebphonePoppedOut: (poppedOut: boolean) => void
  setWebphonePopupSize: ({ width, height }: { width: number; height: number }) => void
  changeWebphonePopupOld: (old: boolean) => void
  changeSelectedContactID: (contactID?: string) => void
}

const initialState: TAppStore = {
  isSidebarMenuExpanded: false,
  userData: {
    ...DEFAULT_USER_DATA,
    xAuth: "",
  },
  _old_webphone: false,
  webphoneExpanded: true,
  webphonePoppedOut: false,
  webphonePopup: {
    height: 600,
    width: 420,
  },
}

export const createAppStore = (initProps?: Partial<TAppStore>) => {
  return createStore<TAppStoreWithFunctions>()(
    persist(
      (set) => ({
        ...initialState,
        ...initProps,
        changeSidebarMenuState: (setExpanded) => {
          set(() => ({ isSidebarMenuExpanded: setExpanded }))
          appStore$.isSidebarMenuExpanded.set(setExpanded)
        },
        setUserData: (userData) => {
          set(() => ({ userData }))
          appStore$.userData.set(userData)
        },
        setWebphoneExpanded: (expanded) => {
          setCookie("webphoneExpanded", expanded, {
            httpOnly: false,
          })
          set(() => ({ webphoneExpanded: expanded }))
          appStore$.webphoneExpanded.set(expanded)
        },
        setWebphonePoppedOut: (poppedOut) => {
          set(() => ({ webphonePoppedOut: poppedOut }))
          appStore$.webphonePoppedOut.set(poppedOut)
        },
        setWebphonePopupSize: ({ width, height }) => {
          set(() => ({ webphonePopup: { width, height } }))
          appStore$.webphonePopup.set({ width, height })
        },
        changeWebphonePopupOld: (old) => {
          set(() => ({
            _old_webphone: old,
          }))
          appStore$._old_webphone.set(old)
        },
        changeSelectedContactID: (contactID) => {
          set(() => ({ webphoneSelectedContactID: contactID }))
          appStore$.webphoneSelectedContactID.set(contactID)
        },
      }),
      {
        name: "appstore-channel",
        partialize: (state) => Object.fromEntries(Object.entries(state).filter(([key]) => !["_old_webphone", "webphoneExpanded"].includes(key))),
      }
    )
  )
}

export const AppStoreContext = createContext<ReturnType<typeof createAppStore> | null>(null)

type AppStoreProviderProps = React.PropsWithChildren<Partial<TAppStore>>

export function AppStoreProvider({ children, ...props }: AppStoreProviderProps) {
  const storeRef = useRef<ReturnType<typeof createAppStore>>(null)
  if (!storeRef.current) {
    storeRef.current = createAppStore(props)
  }
  return <AppStoreContext.Provider value={storeRef.current}>{children}</AppStoreContext.Provider>
}

export function useAppStoreContext<T>(selector: (state: TAppStoreWithFunctions) => T): T {
  const store = useContext(AppStoreContext)
  if (!store) throw new Error("Missing AppStoreContext.Provider in the tree")
  return useStore(store, selector)
}
